import React, { useCallback } from 'react'
import { cn, Checkbox, TextButton, RectangleButton, Dialog } from '@yanolja-rnd/nol-design-system'
import type { CheckboxChangeEvent } from '@yanolja-rnd/nol-design-system'
import { useInView } from 'react-intersection-observer'
import useDomesticCartOptions from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-options'
import useDomesticCartDelete from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-delete'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import useModalHash from '~/app/components/common/useModalHash'

/**
 * CartSelectBar Component
 * 장바구니 상품 전체 선택 및 선택 삭제 기능을 제공하는 컴포넌트
 */
const CartSelectBar = () => {
  /** 선택한 상품 삭제 확인 다이얼로그 표시 여부 */
  const [isSelectedItemDeleteDialogOpen, setSelectedItemDeleteDialogOpen] = useModalHash({
    id: 'remove-selected-confirm-dialog',
  })
  /** 구매불가 상품 삭제 확인 다이얼로그 표시 여부 */
  const [isUnavailableItemDeleteDialogOpen, setUnavailableItemDeleteDialogOpen] = useModalHash({
    id: 'remove-unavailable-confirm-dialog',
  })
  const { deleteCartOptions } = useDomesticCartDelete()

  const { unavailableOptionIds, hasUnavailableOptions, allAvailableOptionIds, totalOptionCount } =
    useDomesticCartOptions()

  const {
    selectedOptionIds,
    selectedOptionCount,
    selectAllOptions,
    unselectAllOptions,
    unselectOptions,
  } = useDomesticCartStore(
    ({ selectedOptionIds, selectAllOptions, unselectAllOptions, unselectOptions }) => {
      return {
        selectedOptionIds,
        selectedOptionCount: selectedOptionIds.size,
        selectAllOptions,
        unselectAllOptions,
        unselectOptions,
      }
    }
  )

  const isAllSelected = totalOptionCount > 0 && selectedOptionCount === totalOptionCount

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent) => {
      if (event.target.checked) {
        selectAllOptions(allAvailableOptionIds ?? [])
      } else {
        unselectAllOptions()
      }
    },
    [selectAllOptions, unselectAllOptions, allAvailableOptionIds]
  )

  const onRemoveSelected = useCallback(() => {
    if (selectedOptionCount > 0) {
      setSelectedItemDeleteDialogOpen(true)
    }
  }, [selectedOptionCount, setSelectedItemDeleteDialogOpen])

  const executeRemoveSelected = useCallback(() => {
    setSelectedItemDeleteDialogOpen(false)
    const optionIdsToRemove = Array.from(selectedOptionIds)

    // Optimistic update: 선택된 옵션들을 즉시 store에서 제거
    unselectOptions(optionIdsToRemove)

    deleteCartOptions(optionIdsToRemove, {
      onError: () => {
        // 에러 시: store 롤백
        selectAllOptions(optionIdsToRemove)
      },
    })
  }, [
    selectedOptionIds,
    unselectOptions,
    selectAllOptions,
    deleteCartOptions,
    setSelectedItemDeleteDialogOpen,
  ])

  const onRemoveUnavailable = useCallback(() => {
    if (hasUnavailableOptions) {
      setUnavailableItemDeleteDialogOpen(true)
    }
  }, [hasUnavailableOptions, setUnavailableItemDeleteDialogOpen])

  const executeRemoveUnavailable = useCallback(() => {
    // Optimistic update: 구매불가 옵션들을 즉시 store에서 제거
    unselectOptions(unavailableOptionIds)

    deleteCartOptions(unavailableOptionIds, {
      onError: () => {
        // 에러 시: store 롤백
        selectAllOptions(unavailableOptionIds)
      },
      onSettled: () => {
        setUnavailableItemDeleteDialogOpen(false)
      },
    })
  }, [
    unavailableOptionIds,
    unselectOptions,
    selectAllOptions,
    deleteCartOptions,
    setUnavailableItemDeleteDialogOpen,
  ])

  // 스크롤 위치 감지를 위한 ref와 inView 상태
  const { ref: topSentinelRef, inView: isTop } = useInView({
    initialInView: true,
  })

  return (
    <>
      {/* 스크롤 위치 감지를 위한 요소 */}
      <div ref={topSentinelRef} className="absolute top-0 h-0 w-full" />

      <div
        className={cn(
          'fixed top-52 z-30 flex w-full max-w-mobile-size items-center justify-between gap-8 bg-static-white px-16 py-10 pc:static pc:max-w-none',
          'pc:static pc:top-auto pc:max-w-none pc:rounded-16 pc:px-24 pc:py-16 pc:shadow-none',
          {
            'shadow-[0px_6px_8px_-6px_rgba(0,0,0,0.2)]': !isTop,
          }
        )}
      >
        <div className="flex items-center gap-8">
          <Checkbox
            checked={isAllSelected}
            onChange={handleCheckboxChange}
            aria-label="전체 선택"
            size={{
              mobile: 'small',
              pc: 'medium',
            }}
          />
          <div className="flex items-center gap-6">
            <div className="text-text-neutral-main typography-body-14-regular">전체선택</div>
            <div className="text-text-neutral-main typography-body-14-bold">
              {selectedOptionCount}/{totalOptionCount}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-12">
          {hasUnavailableOptions && (
            <TextButton
              theme="underline"
              mobileSize="small"
              pcSize="medium"
              weight="regular"
              onClick={onRemoveUnavailable}
            >
              구매불가 삭제
            </TextButton>
          )}

          <RectangleButton
            theme="outlined_basic"
            size={{
              mobile: 'small',
              pc: 'medium',
            }}
            weight="regular"
            onClick={onRemoveSelected}
            aria-label="선택 삭제"
            disabled={selectedOptionCount === 0}
          >
            선택 삭제
          </RectangleButton>
        </div>
      </div>

      <div className="h-54 pc:h-0" />
      {/** 선택 삭제 확인 다이얼로그 */}
      <Dialog
        id="remove-selected-confirm-dialog"
        show={isSelectedItemDeleteDialogOpen}
        title={
          selectedOptionCount === 1
            ? '선택한 상품을 삭제할까요?'
            : `선택한 ${selectedOptionCount}개의 상품을 삭제할까요?`
        }
        negativeButton={{
          text: '아니요',
          onClick: () => setSelectedItemDeleteDialogOpen(false),
        }}
        positiveButton={{
          text: '삭제하기',
          onClick: executeRemoveSelected,
        }}
      />

      {/** 구매불가 삭제 확인 다이얼로그 */}
      <Dialog
        id="remove-unavailable-confirm-dialog"
        show={isUnavailableItemDeleteDialogOpen}
        title="구매할 수 없는 상품을 모두 삭제할까요?"
        negativeButton={{
          text: '아니요',
          onClick: () => setUnavailableItemDeleteDialogOpen(false),
        }}
        positiveButton={{ text: '삭제하기', onClick: executeRemoveUnavailable }}
      />
    </>
  )
}

export default CartSelectBar
